import React, { useState, useEffect } from 'react';
import { X, Menu } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NavigationMenu = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/site-config`)
      .then(r => r.json())
      .then(d => {
        const items = (d.menuItems || [])
          .filter(i => i.visible)
          .sort((a, b) => a.order - b.order);
        setMenuItems(items);
      })
      .catch(() => {
        setMenuItems([
          { slug: 'presentacion', label: 'Presentacion' },
          { slug: 'sinopsis', label: 'Sinopsis' },
          { slug: 'trailer', label: 'Trailer' },
          { slug: 'ficha', label: 'Ficha' },
          { slug: 'sobre-la-pelicula', label: 'Sobre la Pelicula' },
          { slug: 'entradas', label: 'Comprar Entradas' },
        ]);
      });
  }, [isOpen]);

  const handleNavigate = (slug) => {
    onClose();
    if (slug === 'entradas') {
      navigate('/entradas');
    } else if (slug === 'trailer') {
      navigate('/trailer');
    } else {
      navigate(`/page/${slug}`);
    }
  };

  const getPath = (slug) => {
    if (slug === 'entradas') return '/entradas';
    if (slug === 'trailer') return '/trailer';
    return `/page/${slug}`;
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
          onClick={onClose}
          data-testid="menu-backdrop"
        />
      )}

      {/* Menu Panel */}
      <div
        className={`fixed top-0 left-0 h-full w-80 bg-[#0d1620]/95 backdrop-blur-xl z-[70] transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="navigation-menu"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-10 h-10 rounded-full border border-[#f5f5dc]/20 flex items-center justify-center text-[#f5f5dc]/60 hover:text-[#f5f5dc] hover:border-[#f5f5dc]/40 transition-colors"
          data-testid="close-menu"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col h-full pt-20 pb-10">
          {/* Title */}
          <div className="px-8 mb-8 relative z-10">
            <h2 className="font-metro text-4xl text-[#f5f5dc]">Los Legendarios</h2>
            <p className="font-condensed text-sm text-[#f5f5dc]/40 tracking-widest uppercase mt-2">
              Guillaume Ivernel
            </p>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 px-6 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const path = getPath(item.slug);
              const isActive = location.pathname === path;
              return (
                <button
                  key={item.slug}
                  onClick={() => handleNavigate(item.slug)}
                  className={`w-full text-left px-4 py-3 rounded-lg font-condensed text-base tracking-wider uppercase transition-all ${
                    isActive
                      ? 'bg-[#50c878]/20 text-[#50c878]'
                      : 'text-[#f5f5dc]/60 hover:text-[#f5f5dc] hover:bg-[#f5f5dc]/5'
                  }`}
                  data-testid={`nav-item-${item.slug}`}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="px-8 mt-auto">
            <div className="pt-6 border-t border-[#f5f5dc]/10">
              <p className="font-condensed text-xs text-[#f5f5dc]/30 tracking-widest uppercase text-center">
                Estreno en Cines 15 de Mayo 2026
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const MenuButton = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setMenuOpen(true)}
        className="w-12 h-12 rounded-full bg-[#50c878] flex items-center justify-center hover:bg-[#40a0a0] transition-all hover:scale-105"
        data-testid="menu-button"
      >
        <Menu className="w-6 h-6 text-white" />
      </button>
      <NavigationMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
};

export default NavigationMenu;
